<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { Icon } from '@iconify/vue';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';

interface DynamicDocument {
  label: string;
  description: string;
  type: 'section14' | 'section201-attestation' | 'section201-declaration' | 'cdd';
}

interface RepeatingSectionMeta {
  sectionName: string;
  columns?: Array<{ varKey: string; varLabel?: string }>;
}

interface SignerInfo {
  name: string;
  email: string;
  role: string;
}

// prepare-documents payload 구성에 사용되는 내부 문서 구조.
// 생성 후에는 PreSetTaskDetailPage 로 이동해 그 화면이 DB 기준으로 다시 렌더링하므로
// UI 용 카운트/포맷 필드는 여기서 계산하지 않는다.
interface MappedDocument {
  label: string;
  templateName: string;
  templateId: number;
  personName: string;
  formData: Record<string, any>;
  variables: Array<{ varKey: string; varLabel: string; varType?: string; isRequired?: boolean }>;
  repeatingSections?: RepeatingSectionMeta[];
  signers?: SignerInfo[];
  entityCd?: string;
}

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  /** Edit 모드 — 값이 있으면 기존 task 수정(Update). 호스트가 restoreState 로 입력 복원. */
  editTaskId?: number | null;
}>();

// Edit(수정) 모드 — 버튼 라벨 'Update Task' + create→update 분기.
const editMode = computed(() => !!props.editTaskId);

// restoreState 직후 입력 스냅샷(기준선). Update Task 는 실제 변경이 있을 때만 활성화.
const editBaseline = ref<string | null>(null);
const isDirty = computed(() => {
  if (!editMode.value) return true; // Create 모드는 항상 활성
  if (editBaseline.value == null) return false; // 기준선 캡처 전 — 비활성
  try { return JSON.stringify(gatherState()) !== editBaseline.value; } catch { return true; }
});

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'create-task', data: { taskTitle: string }): void;
  (e: 'preview-doc', docIndex: number, doc: MappedDocument): void;
  (e: 'edit-doc', docIndex: number, doc: MappedDocument): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string; docs: MappedDocument[] }): void;
}>();

const router = useRouter();

// Task Title 입력
/**
 * 문서 라벨(=파일명)에 들어갈 이름 다듬기.
 * 말레이시아 법인명은 거의 전부 "SDN. BHD." 처럼 마침표로 끝나서
 * 그대로 붙이면 "CDD - QA HOLDINGS SDN. BHD..docx" 가 된다(점 두 개).
 * 파일명 끝의 마침표/공백만 떼고 이름 가운데 마침표는 그대로 둔다.
 */
function docSafeName(name: string): string {
  return String(name || '').trim().replace(/[.\s]+$/, '');
}

// 좌측 챗에서 받은 Task Title(props.taskTitle)이 우측 필드에 반영되지 않던 원인:
// ref 초기값으로 프리셋 이름을 한 번 박아 두면, 챗 제목은 그 뒤에 도착하므로 영영 무시된다.
// 사용자가 이 칸을 직접 고치면 그 값이 최우선이고, 아니면 챗 제목 -> 프리셋 이름 순으로 따른다.
const taskTitleEdited = ref<string | null>(null);
const taskTitleInput = computed<string>({
  get: () => taskTitleEdited.value ?? (props.taskTitle || props.selection.task.TASK_NAME || ''),
  set: (v: string) => { taskTitleEdited.value = v; },
});
// 반대 방향도 막혀 있었다 — 우측 칸을 한 번이라도 건드리면(빈 문자열로 지워도)
// taskTitleEdited 가 null 이 아니게 되어 그 뒤 **챗에서 고친 제목이 영영 무시**됐다.
// 챗에서 제목이 실제로 바뀌면 그건 사용자의 명시적 행동이므로 그 값이 이긴다.
watch(() => props.taskTitle, (v, prev) => {
  if (v && v !== prev) taskTitleEdited.value = null;
});
const creating = ref(false);
const prepareProgress = ref('');

// 진행 상태를 호스트(PreSetDocGenView)의 공통 중앙 오버레이로 흘려보낸다.
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; presetProgress.message = v ? 'Creating task…' : ''; });

async function handleCreateTask() {
  if (!taskTitleInput.value.trim() || !props.entityData) return;
  creating.value = true;
  prepareProgress.value = 'Loading templates...';

  try {
    const icpCdVal = props.entityData.icpCd;
    let dirs = props.entityData.directors || [];
    let shrs = props.entityData.shareholders || [];

    // 1. 템플릿 + 변수 조회 — fetch 직접 사용
    console.log('[PreSetIncorporation] Loading templates...');
    const tmplResponse = await fetch('/api/aiDocuments/incorporation/templates-with-variables', {
      credentials: 'include',
    });
    const tmplRes = await tmplResponse.json();
    console.log('[PreSetIncorporation] tmplRes:', tmplRes?.success, tmplRes?.templates?.length);
    if (!tmplRes?.success || !tmplRes.templates?.length) {
      prepareProgress.value = `Failed to load templates. ${tmplRes?.message || ''}`;
      creating.value = false;
      return;
    }

    prepareProgress.value = `Loaded ${tmplRes.templates.length} templates. Mapping...`;

    // 2. Entity 데이터 — fetch 직접 사용
    let incorporationData: Record<string, any> = {};
    try {
      console.log('[PreSetIncorporation] Loading entity data for:', icpCdVal);
      const entityResponse = await fetch(`/api/aiDocuments/incorporation/entity-data/${icpCdVal}`, {
        credentials: 'include',
      });
      const entityRes = await entityResponse.json();
      if (entityRes?.success) {
        incorporationData = entityRes.data?.incorporationData || {};
        if (entityRes.data?.directors?.length) dirs = entityRes.data.directors;
        if (entityRes.data?.shareholders?.length) shrs = entityRes.data.shareholders;
      }
    } catch (e: any) {
      console.warn('[PreSetIncorporation] Entity data load failed:', e.message);
    }

    // 3. 변수 매핑
    const { autoMapVariables } = await import('../../incorporation/composables/incorporationVariableMapper');
    const docs: MappedDocument[] = [];

    // QA state 구성 (Step2IncorporationQA의 store.incorporationQA 대체)
    const qa = {
      icpCd: icpCdVal,
      companyName: props.entityData.companyName || '',
      directors: dirs.map((d: any) => ({
        name: d.name, nationality: d.nationality, isForeign: d.isForeign,
        email: d.email, idNo: d.idNo, idType: d.idType, contactCd: '',
      })),
      shareholders: shrs.map((s: any) => ({
        // 법인 주주는 개인 이름이 비어 있다 - 법인명으로 대신한다.
        // SSM 미등록 법인(외국법인·정부기관 등)은 corporateName 이 아니라 entityName 에 이름이 있다.
        name: s.name
          || s.corporateName
          || s.myShareholderDetails?.corporateName
          || s.myDetails?.corporateName
          || s.entityName
          || s.myShareholderDetails?.entityName
          || s.myDetails?.entityName
          || '',
        email: s.email, shares: s.numberOfShares,
        shareType: s.classOfShare, contactCd: '',
        // CDD 서식을 개인용/법인용 중에 고르려면 여기까지 종류가 따라와야 한다.
        kind: s.kind || s.myShareholderKind || 'Individual',
      })),
      useStandardTemplates: true,
      hasForeignDirectors: dirs.some((d: any) => d.isForeign),
      proceedWithEFiling: false,
      editBeforeGenerate: false,
      currentQuestion: 4,
      taskTitleConfirmed: true,
    };

    // 템플릿 분류
    // DOC_TYPE 기준 분류: TMP001=Section201, TMP002=Section201 Attestation, TMP003=Section14,
    // TMP004=CDD(Legal Person), TMP005=CDD(Legal Arrangement), TMP006=CDD(Individual)
    const declarationTmpl = tmplRes.templates.find((t: any) => t.docType === 'TMP001');
    const attestationTmpl = tmplRes.templates.find((t: any) => t.docType === 'TMP002');
    const section14Tmpl = tmplRes.templates.find((t: any) => t.docType === 'TMP003');
    // CDD 는 대상에 따라 서식이 다르다. 예전에는 find 로 목록의 첫 항목 하나만 집었는데,
    // 템플릿이 파일명 오름차순으로 오다 보니 Individual 이 늘 먼저 걸려
    // **법인 주주에게도 개인용 서식**이 생성되고 있었다(법인 정보를 담을 자리가 없는 서식).
    const cddIndividualTmpl = tmplRes.templates.find((t: any) => t.docType === 'TMP006');
    const cddLegalPersonTmpl = tmplRes.templates.find((t: any) => t.docType === 'TMP004');
    const cddTmpl = cddIndividualTmpl || cddLegalPersonTmpl;

    // CDD — 중복 제거
    if (cddTmpl) {
      const schema = (cddTmpl.variables || []).map((v: any) => ({
        varKey: v.varKey || v, varLabel: v.varLabel || v, varType: v.varType || 'text', isRequired: v.isRequired ?? true,
      }));
      const normName = (n: string) => n.trim().toLowerCase().split(/\s+/).sort().join(' ');
      const seen = new Set<string>();
      const allPersons = [...qa.directors.map((d: any) => ({ ...d, context: 'director' as const })), ...qa.shareholders.map((s: any) => ({ ...s, context: 'shareholder' as const }))];

      for (const person of allPersons) {
        const key = normName(person.name);
        if (seen.has(key)) continue;
        seen.add(key);
        const personIdx = person.context === 'director'
          ? qa.directors.findIndex((d: any) => normName(d.name) === key)
          : qa.shareholders.findIndex((s: any) => normName(s.name) === key);
        const isCorporate = String((person as any).kind || '') === 'BodyCorporate';
        const tmplForPerson = (isCorporate ? cddLegalPersonTmpl : cddIndividualTmpl) || cddTmpl;
        const schemaForPerson = ((tmplForPerson.variables || []) as any[]).map((v: any) => ({
          varKey: v.varKey || v, varLabel: v.varLabel || v, varType: v.varType || 'text', isRequired: v.isRequired ?? true,
        }));
        const formData = autoMapVariables(schemaForPerson, incorporationData, qa as any, person.context, Math.max(0, personIdx));
        // 디버그: signer 매핑 확인
        const signerEntries = Object.entries(formData).filter(([k]) => /signer/i.test(k));
        console.log(`[Signer Debug] CDD ${person.name}:`, signerEntries.map(([k, v]) => `${k}=${typeof v === 'string' && v.startsWith('[') ? 'JSON' : v}`));
        const cddRS = (tmplForPerson.repeatingSections || []) as RepeatingSectionMeta[];
        const cddSigners: SignerInfo[] = [{
          name: person.name,
          email: (person as any).email || '',
          role: 'signer',
        }];
        docs.push({
          label: `CDD — ${docSafeName(person.name)}`, templateName: tmplForPerson.fileName, templateId: tmplForPerson.templateId,
          personName: person.name, formData, variables: schemaForPerson,
          repeatingSections: cddRS, signers: cddSigners, entityCd: icpCdVal,
        });
      }
    }

    // Section 201 per Director
    for (let i = 0; i < qa.directors.length; i++) {
      const dir = qa.directors[i];
      const tmpl = dir.isForeign ? attestationTmpl : declarationTmpl;
      if (!tmpl) continue;
      const schema = (tmpl.variables || []).map((v: any) => ({
        varKey: v.varKey || v, varLabel: v.varLabel || v, varType: v.varType || 'text', isRequired: v.isRequired ?? true,
      }));
      const formData = autoMapVariables(schema, incorporationData, qa as any, 'director', i);
      const typeName = dir.isForeign ? 'Attestation' : 'Declaration';
      const s201RS = (tmpl.repeatingSections || []) as RepeatingSectionMeta[];
      const s201Signers: SignerInfo[] = [{
        name: dir.name,
        email: dir.email || '',
        role: 'signer',
      }];
      docs.push({
        label: `Section 201 (${typeName}) — ${dir.name}`, templateName: tmpl.fileName, templateId: tmpl.templateId,
        personName: dir.name, formData, variables: schema,
        repeatingSections: s201RS, signers: s201Signers, entityCd: icpCdVal,
      });
    }

    // Section 14
    if (section14Tmpl) {
      const schema = (section14Tmpl.variables || []).map((v: any) => ({
        varKey: v.varKey || v, varLabel: v.varLabel || v, varType: v.varType || 'text', isRequired: v.isRequired ?? true,
      }));
      const formData = autoMapVariables(schema, incorporationData, qa as any, 'section14');
      const s14RS = (section14Tmpl.repeatingSections || []) as RepeatingSectionMeta[];
      // Section 14: 모든 director 서명 (SSM Super Form 은 director 전원 서명이 일반적)
      const s14Signers: SignerInfo[] = qa.directors
        .filter((d: any) => d?.name)
        .map((d: any) => ({ name: d.name, email: d.email || '', role: 'signer' }));
      docs.push({
        label: 'Section 14 (SSM Super Form)', templateName: section14Tmpl.fileName, templateId: section14Tmpl.templateId,
        personName: qa.companyName, formData, variables: schema,
        repeatingSections: s14RS, signers: s14Signers, entityCd: icpCdVal,
      });
    }

    prepareProgress.value = `${docs.length} documents mapped. Creating task...`;

    // Task 생성
    const taskTypeCd = props.selection.task.TASK_CODE;
    if (!taskTypeCd) {
      prepareProgress.value = 'Error: Task Code not configured in PreSet. Please set TASK_CODE in Admin.';
      creating.value = false;
      return;
    }

    // officerData → TASK_DATA.formData 로 저장 (presetForm 은 Edit 복원 스냅샷).
    const officerData = {
      entity: icpCdVal || '',
      entityName: entityName.value || '',
      presetForm: gatherState(),
    };

    // Task 생성/수정 — fetch 사용 (axios interceptor 리다이렉트 방지)
    let taskId: number | null = null;
    if (editMode.value) {
      // ── Update 분기 ──
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { creating.value = false; return; }
      try {
        await fetch(`/api/startTask/tasks/${taskId}/data`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ taskData: { formData: officerData } }),
        });
      } catch (err) {
        console.error('[PreSetIncorporation] task data update failed:', err);
        creating.value = false; return;
      }
      try {
        await fetch(`/api/startTask/tasks/${taskId}/title`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ taskTitle: taskTitleInput.value.trim() }),
        });
      } catch (err) { console.error('[PreSetIncorporation] task title update failed:', err); }
    } else {
      // ── Create 분기 ──
      try {
        const taskRes = await fetch('/api/startTask/tasks', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            entityCd: icpCdVal,
            taskTypeCd,
            taskTypeName: props.selection.task.TASK_NAME,
            taskTitle: taskTitleInput.value.trim(),
            taskFlow: { createDocument: true, requestTSign: false, proceedWithEFiling: false },
            officerData,
          }),
        });
        const taskJson = await taskRes.json();
        console.log('[PreSetIncorporation] createTask response:', taskJson);
        taskId = taskJson?.data?.taskId || taskJson?.taskId || null;
        if (!taskId) {
          prepareProgress.value = `Error: ${taskJson?.error?.message || taskJson?.message || 'Task creation failed'}`;
          creating.value = false;
          return;
        }
      } catch (taskErr: any) {
        prepareProgress.value = `Error creating task: ${taskErr.message}`;
        creating.value = false;
        return;
      }
    }
    prepareProgress.value = `Task ${editMode.value ? 'updated' : 'created'} (ID: ${taskId}). Saving documents...`;

    // 문서 DB 저장 (L_DOC_CUST_SAVE) — fetch 직접 사용
    try {
      const saveResponse = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          taskId,
          replaceExisting: editMode.value,
          documents: docs.map(d => {
            // 우선순위: doc.signers (명시적 1인/다수 서명자) > formData.signers JSON
            let signersOut: SignerInfo[] | undefined;
            if (Array.isArray(d.signers) && d.signers.length > 0) {
              signersOut = d.signers;
            } else if (d.formData.signers) {
              try {
                const parsed = typeof d.formData.signers === 'string'
                  ? JSON.parse(d.formData.signers)
                  : d.formData.signers;
                if (Array.isArray(parsed) && parsed.length > 0) signersOut = parsed;
              } catch { /* ignore */ }
            }
            return {
              templateId: d.templateId,
              label: d.label,
              personName: d.personName,
              formData: d.formData,
              signers: signersOut,
              entityCd: icpCdVal,
            };
          }),
        }),
      });
      const saveRes = await saveResponse.json();
      console.log('[PreSetIncorporation] saveRes:', saveRes);

      if (saveRes?.success) {
        for (const r of (saveRes.results || [])) {
          if (r.success && docs[r.index]) {
            (docs[r.index] as any).saveId = r.saveId;
          }
        }
        prepareProgress.value = `${saveRes.successCount || docs.length} documents saved.`;
      } else {
        prepareProgress.value = `Documents prepared (save failed: ${saveRes?.message || 'unknown'})`;
      }
    } catch (saveErr: any) {
      console.warn('[PreSetIncorporation] Document save failed:', saveErr);
      prepareProgress.value = `Documents prepared (save error: ${saveErr.message})`;
    }

    // Task document status 업데이트 (초록불)
    if (taskId) {
      try {
        await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
      } catch { /* ignore */ }
    }

    // PreSet 사용 로그 — fetch 직접 사용
    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }),
      });
    } catch { /* ignore */ }

    // Task + 문서 생성 완료 → PreSetTaskDetailPage 로 이동 (상세 화면은 DB 기준으로 다시 렌더링)
    emit('task-created', { taskId, taskTitle: taskTitleInput.value.trim(), docs });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (err: any) {
    console.error('[PreSetIncorporation] prepareDocs failed:', err);
    prepareProgress.value = `Error: ${err.message}`;
  } finally {
    creating.value = false;
  }
}

const companySuffix = computed(() => {
  const code = props.entityData?.companySuffix || '';
  // M31001=SDN. BHD., M31002=BHD. 등
  const suffixMap: Record<string, string> = {
    M31001: 'SDN. BHD.', M31002: 'BHD.', M31003: 'LTD.', M31004: 'PTE. LTD.',
  };
  return suffixMap[code] || code;
});
const entityName = computed(() => {
  const name = props.entityData?.companyName || props.entityData?.entityName || '';
  return companySuffix.value ? `${name} ${companySuffix.value}` : name;
});
const icpCd = computed(() => props.entityData?.icpCd || props.entityData?.entityCd || '');
const regNo = computed(() => props.entityData?.regNo || props.entityData?.registrationNo || '');

/** entity-data API — Edit 시 tlinkData 에 directors 가 없을 때 UI·문서 생성 보강 */
const entityEnrichment = ref<Record<string, any> | null>(null);

async function refreshEntityEnrichment() {
  const icp = icpCd.value;
  if (!icp) return;
  try {
    const res = await fetch(`/api/aiDocuments/incorporation/entity-data/${icp}`, { credentials: 'include' });
    const json = await res.json();
    if (json?.success) entityEnrichment.value = json.data || null;
  } catch (e: any) {
    console.warn('[PreSetIncorporation] entity-data refresh failed:', e?.message);
  }
}

watch(icpCd, (v) => { if (v) void refreshEntityEnrichment(); }, { immediate: true });

const resolvedEntityData = computed(() => {
  const base = props.entityData || {};
  const enr = entityEnrichment.value;
  if (!enr) return base;
  return {
    ...base,
    icpCd: base.icpCd || enr.icpCd,
    companyName: enr.companyName || base.companyName,
    directors: (enr.directors?.length ? enr.directors : base.directors) || [],
    shareholders: (enr.shareholders?.length ? enr.shareholders : base.shareholders) || [],
  };
});

const directors = computed(() => resolvedEntityData.value?.directors || []);
const shareholders = computed(() => resolvedEntityData.value?.shareholders || []);

/** T-Link 데이터 기반 동적 문서 목록 */
const dynamicDocuments = computed<DynamicDocument[]>(() => {
  if (!props.entityData) return [];
  const docs: DynamicDocument[] = [];

  // 1. Section 14 — 항상 1건
  docs.push({
    label: 'Section 14',
    description: 'Company Constitution',
    type: 'section14',
  });

  // 2. Section 201 per Director — 외국인: Attestation, 내국인: Declaration
  for (const d of directors.value) {
    if (d.isForeign) {
      docs.push({
        label: `Section 201 with Attestation — ${d.name}`,
        description: 'Notarized declaration (Foreign Director)',
        type: 'section201-attestation',
      });
    } else {
      docs.push({
        label: `Section 201 Declaration — ${d.name}`,
        description: 'Standard declaration (Local Director)',
        type: 'section201-declaration',
      });
    }
  }

  // 3. CDD per Director/Shareholder (동일인 중복 제거 — 이름 정규화 비교)
  const normalizeName = (name: string) => name.trim().toLowerCase().split(/\s+/).sort().join(' ');
  const cddNormalized = new Set<string>();
  const addCdd = (name: string, kind: string) => {
    if (!name) return;
    const key = normalizeName(name);
    if (cddNormalized.has(key)) return;
    cddNormalized.add(key);
    docs.push({
      label: `CDD — ${docSafeName(name)}`,
      description: `Client Due Diligence (${kind})`,
      type: 'cdd',
    });
  };
  // 법인 주주는 entity-data 응답의 name 이 비어 있고 corporateName 에만 값이 있다.
  // 예전에는 그대로 넘겨 법인 CDD 문서가 통째로 빠졌다(addCdd 는 빈 이름을 조용히 버린다).
  // SSM 미등록 법인(외국법인·정부기관 등)은 corporateName 도 비고 entityName 에만 이름이 있다.
  const displayName = (p: any) => String(
    p?.name
    || p?.corporateName || p?.myShareholderDetails?.corporateName || p?.myDetails?.corporateName
    || p?.entityName || p?.myShareholderDetails?.entityName || p?.myDetails?.entityName
    || '',
  ).trim();
  for (const d of directors.value) addCdd(displayName(d), 'Individual');
  for (const s of shareholders.value) {
    const kind = s.kind || s.myShareholderKind || 'Individual';
    addCdd(displayName(s), kind === 'BodyCorporate' ? 'Legal Person' : 'Individual');
  }

  return docs;
});

const deepLinkUrl = computed(() => {
  if (!icpCd.value) return '';
  return `/setup-entity/detail/${icpCd.value}`;
});

// T-Sign / E-Filing / 편집 / 다운로드 / 타이틀 수정은 모두 PreSetTaskDetailPage 에서 처리.
// 이 패널은 "문서 매핑 + prepare-documents + 상세 페이지로 라우팅" 까지만 담당.

// ─── Edit 모드 상태 스냅샷 ───
//   이 패널의 유일한 사용자 입력 필드는 Task Title(taskTitleInput) 뿐 — 나머지(회사/이사/주주/문서)는
//   모두 props.entityData 에서 파생되므로 사용자가 직접 편집하지 않는다.
function gatherState(): Record<string, any> {
  return {
    taskTitleInput: taskTitleEdited.value,
  };
}

function restoreState(s: Record<string, any> | null | undefined) {
  if (!s || typeof s !== 'object') return;
  if (s.taskTitleInput != null) taskTitleEdited.value = s.taskTitleInput;
  // 입력 정착 후 기준선 캡처 → 이후 실제 변경만 dirty 로 감지.
  nextTick(() => { editBaseline.value = JSON.stringify(gatherState()); });
}

defineExpose({ gatherState, restoreState });
</script>

<template>
  <div class="inc-panel">
    <!-- 헤더 -->
    <div class="inc-header">
      <div>
        <div class="inc-title">{{ selection.task.TASK_NAME }}</div>
        <div class="inc-subtitle">Incorporation Document Setup</div>
      </div>
      <v-btn variant="text" size="small" @click="emit('back')">
        <v-icon start size="16">mdi-arrow-left</v-icon>
        Back
      </v-btn>
    </div>

    <!-- 선택된 Entity 없음 -->
    <div v-if="!entityData" class="inc-empty">
      <Icon icon="mdi:link-variant-plus" width="48" color="#d0d0d0" class="mb-3" />
      <div style="font-size: 15px; font-weight: 600; color: #666;">Select an entity from T-Link</div>
      <div style="font-size: 12px; color: #999; margin-top: 4px;">
        Use the chatbot on the left to select a T-Link entity, or skip to proceed with Q&A.
      </div>
    </div>

    <!-- 선택된 Entity 정보 — 생성 전에만 표시 (생성 후에는 PreSetTaskDetailPage 로 이동) -->
    <div v-else class="inc-content">
      <!-- Entity 카드 -->
      <div class="inc-entity-card">
        <div class="d-flex align-center justify-space-between">
          <div class="d-flex align-center ga-3">
            <div class="inc-entity-icon">
              <Icon icon="mdi:domain" width="24" color="#1B84FF" />
            </div>
            <div>
              <div class="inc-entity-name">{{ entityName }}</div>
              <div v-if="regNo" class="inc-entity-reg">{{ regNo }}</div>
            </div>
          </div>
          <!-- 딥링크 -->
          <router-link v-if="deepLinkUrl" :to="deepLinkUrl" target="_blank" class="inc-deeplink">
            <Icon icon="mdi:open-in-new" width="14" class="mr-1" />
            View Detail
          </router-link>
        </div>
      </div>

      <!-- Directors & Shareholders (한 줄) -->
      <div class="inc-section">
        <div class="inc-section-header">
          <Icon icon="mdi:account-group" width="16" color="#5c6bc0" />
          <span>Directors ({{ directors.length }}) · Shareholders ({{ shareholders.length }})</span>
        </div>
        <div class="inc-persons-row">
          <v-chip
            v-for="(d, idx) in directors" :key="'d'+idx"
            size="small" variant="tonal" color="indigo" class="mr-1 mb-1"
          >
            <v-icon start size="14">mdi-account-tie</v-icon>
            {{ d.name }}
            <span v-if="d.nationality" class="ml-1" style="font-size:10px;opacity:0.7">{{ d.nationality }}</span>
            <v-chip v-if="d.isForeign" size="x-small" color="warning" variant="flat" class="ml-1" style="height:16px;">F</v-chip>
          </v-chip>
          <v-chip
            v-for="(s, idx) in shareholders" :key="'s'+idx"
            size="small" variant="tonal" color="teal" class="mr-1 mb-1"
          >
            <v-icon start size="14">mdi-account-cash</v-icon>
            {{ s.name }}
            <span v-if="s.numberOfShares" class="ml-1" style="font-size:10px;opacity:0.7">{{ s.numberOfShares }}shares</span>
          </v-chip>
        </div>
      </div>

      <!-- Task Title 입력 -->
      <div class="inc-section">
        <div class="inc-section-header">
          <Icon icon="mdi:text-box-outline" width="16" color="#666" />
          <span>Task Title</span>
        </div>
        <div class="attention-input attention-active">
          <v-text-field
            v-model="taskTitleInput"
            variant="outlined"
            density="compact"
            placeholder="Enter task title..."
            hide-details
            class="ml-6"
          />
        </div>
      </div>

      <!-- 문서 목록 -->
      <div class="inc-section">
        <div class="inc-section-header">
          <Icon icon="mdi:file-document-multiple-outline" width="16" color="#5c6bc0" />
          <span>Documents ({{ dynamicDocuments.length || selection.documents.length }})</span>
        </div>

        <!-- T-Link 선택 시: 동적 문서 목록 -->
        <template v-if="dynamicDocuments.length > 0">
          <div
            v-for="(doc, idx) in dynamicDocuments"
            :key="`${doc.type}-${idx}`"
            class="inc-doc-item"
          >
            <div class="inc-doc-num">{{ idx + 1 }}</div>
            <Icon
              :icon="doc.type === 'cdd' ? 'mdi:account-check-outline' : 'mdi:file-document-outline'"
              width="16"
              :color="doc.type === 'section201-attestation' ? '#e65100' : doc.type === 'cdd' ? '#00897b' : '#5c6bc0'"
            />
            <div>
              <div style="font-size: 12px; font-weight: 500;">{{ doc.label }}</div>
              <div style="font-size: 10px; color: #999;">{{ doc.description }}</div>
            </div>
          </div>
        </template>

        <!-- T-Link 미선택 시: preset 기본 문서 목록 -->
        <template v-else>
          <div
            v-for="(doc, idx) in selection.documents"
            :key="doc.docCode"
            class="inc-doc-item"
          >
            <div class="inc-doc-num">{{ idx + 1 }}</div>
            <Icon icon="mdi:file-document-outline" width="16" color="#5c6bc0" />
            <div>
              <div style="font-size: 12px; font-weight: 500;">{{ doc.templateName }}</div>
              <div style="font-size: 10px; color: #999;">{{ doc.docCode }}</div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- 프로그레스 (문서 생성 중 또는 에러) -->
    <div v-if="creating || prepareProgress" class="inc-progress">
      <v-progress-linear v-if="creating" indeterminate color="primary" height="3" />
      <div class="inc-progress-text" :class="{ 'text-error': prepareProgress.startsWith('Error') }">
        {{ prepareProgress }}
      </div>
    </div>

    <!-- 하단 버튼 — Create Task 버튼 (생성 성공 시 PreSetTaskDetailPage 로 이동) -->
    <div class="inc-footer">
      <v-btn variant="outlined" color="grey" size="small" @click="emit('back')">
        <Icon icon="mdi:arrow-left" width="16" class="mr-1" />
        Back
      </v-btn>
      <v-tooltip :disabled="!(editMode && !isDirty)" location="top" text="No changes to update">
        <template #activator="{ props: tProps }">
          <span v-bind="tProps">
            <v-btn
              variant="flat"
              color="primary"
              size="small"
              :disabled="creating || (editMode && !isDirty)"
              :loading="creating"
              @click="handleCreateTask"
            >
              <Icon icon="mdi:check" width="16" class="mr-1" />
              {{ creating ? (editMode ? 'Updating…' : 'Creating…') : (editMode ? 'Update Task' : 'Create Task') }}
            </v-btn>
          </span>
        </template>
      </v-tooltip>
    </div>

  </div>
</template>

<style scoped>
.inc-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.inc-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 12px;
  border-bottom: 1px solid #f0f0f0;
}
.inc-title {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a2e;
}
.inc-subtitle {
  font-size: 12px;
  color: #888;
  margin-top: 2px;
}

.inc-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.inc-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
}

.inc-entity-card {
  background: #f0f5ff;
  border: 1px solid #d6e4ff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
}
.inc-entity-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}
.inc-entity-name {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
}
.inc-entity-reg {
  font-size: 12px;
  color: #666;
  margin-top: 1px;
}
.inc-deeplink {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #1B84FF;
  text-decoration: none;
  font-weight: 500;
  padding: 4px 8px;
  border-radius: 6px;
  transition: background 0.15s;
}
.inc-deeplink:hover {
  background: rgba(27, 132, 255, 0.08);
}

.inc-section {
  margin-bottom: 16px;
}
.inc-section-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #555;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid #f0f0f0;
}
.inc-person-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
}
.inc-person-num {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #eef2ff;
  color: #5c6bc0;
  font-size: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.inc-persons-row {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
}
.inc-person-name { font-size: 13px; font-weight: 500; color: #333; }
.inc-person-meta { font-size: 11px; color: #888; margin-top: 1px; }

.inc-doc-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
}
.inc-doc-num {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: #eef2ff;
  color: #5c6bc0;
  font-size: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.gen-doc-card {
  transition: all 0.15s;
  cursor: pointer;
}
.gen-doc-card:hover {
  border-color: #90caf9;
  background: #fafcff;
}

/* ── 입력 필드 주목 애니메이션 ── */
.attention-input {
  position: relative;
  border-radius: 6px;
}
.attention-active {
  animation: attention-glow 2s ease-in-out infinite;
}
.attention-active :deep(.v-field__outline) {
  animation: border-shift 3s linear infinite;
}
@keyframes attention-glow {
  0%, 100% { box-shadow: 0 0 0 0 rgba(244, 67, 54, 0); }
  50% { box-shadow: 0 0 8px 2px rgba(244, 67, 54, 0.3); }
}
@keyframes border-shift {
  0% { border-color: rgba(244, 67, 54, 0.3); }
  25% { border-color: rgba(244, 67, 54, 0.7); }
  50% { border-color: rgba(255, 87, 34, 0.5); }
  75% { border-color: rgba(244, 67, 54, 0.7); }
  100% { border-color: rgba(244, 67, 54, 0.3); }
}

.inc-progress {
  padding: 0 24px 8px;
}
.inc-progress-text {
  font-size: 11px;
  color: #888;
  margin-top: 4px;
  text-align: center;
}

.inc-footer {
  display: flex;
  justify-content: space-between;
  padding: 12px 24px;
  border-top: 1px solid #f0f0f0;
}
</style>
